import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { toast } from "sonner";
import type {
  DealerPortalCustomer,
  DealerPortalCustomerType,
} from "@/types/dealerPortal";
import { User, Building, Store, ChevronLeft } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  formatPersonalNumberYYMMDD,
  validatePersonalNumberYYMMDD,
  validateEmailOptional,
  validatePhoneDigitsOnlyMin7,
  validatePostalCodeDigitsOnly,
  validateCityOptional,
  validateCountryOptional,
  validateNameDealerPortal,
} from "@/utils/formValidation";
import { useDealerPortalNativeSheetProps } from "@/lib/native/useDealerPortalNativeSheetProps";
import { getLanguage } from "@/utils/languageUtils";

const PHONE_DIGITS = /^\d*$/;
const NAME_LETTERS_SPACES = /^[A-ZÅÄÖa-zåäö\s]*$/;
const CITY_COUNTRY_LETTERS = /^[A-ZÅÄÖa-zåäö\s]*$/;
const POSTAL_DIGITS = /^\d*$/;

const CUSTOMER_TYPES: {
  value: DealerPortalCustomerType;
  labelKey: keyof typeof CUSTOMER_TYPE_KEYS;
  Icon: typeof User;
}[] = [
  { value: "PRIVATE", labelKey: "typePrivate", Icon: User },
  { value: "DEALER", labelKey: "typeDealer", Icon: Store },
  { value: "BUSINESS", labelKey: "typeBusiness", Icon: Building },
];

const CUSTOMER_TYPE_KEYS = {
  typePrivate: "Privatkund",
  typeDealer: "Handlare",
  typeBusiness: "Företag",
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (customer: DealerPortalCustomer) => void;
  /** Stack above an open dealer-portal / follow-up sheet */
  nested?: boolean;
}

export function DealerPortalCreateCustomerDialog({
  open,
  onClose,
  onSuccess,
  nested = false,
}: Props) {
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const cc = d.createCustomer as Record<string, string>;
  const c = d.customers as Record<string, string>;
  const nativeSheetProps = useDealerPortalNativeSheetProps(
    open,
    "sm:max-w-[1000px] max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]",
  );

  const [step, setStep] = useState<"type" | "form">("type");
  const [customerType, setCustomerType] =
    useState<DealerPortalCustomerType>("PRIVATE");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [personalNumber, setPersonalNumber] = useState("");
  const [street, setStreet] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [companyConnection, setCompanyConnection] = useState("");
  const [orgNumber, setOrgNumber] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [contactPersonEmail, setContactPersonEmail] = useState("");
  const [contactPersonPhone, setContactPersonPhone] = useState("");
  const [isBuyer, setIsBuyer] = useState(false);
  const [isSeller, setIsSeller] = useState(false);

  const isCompanyType =
    customerType === "BUSINESS" || customerType === "DEALER";

  const { createCustomer: createMutation } = useDealerPortal();
  const lang = language ?? getLanguage();

  const validate = () => {
    const nameRes = validateNameDealerPortal(name, undefined, lang);
    if (!nameRes.isValid) {
      toast.error(nameRes.errors[0]);
      return false;
    }
    if (isCompanyType) {
      if (phone.trim()) {
        const phoneRes = validatePhoneDigitsOnlyMin7(phone, undefined, lang);
        if (!phoneRes.isValid) {
          toast.error(phoneRes.errors[0]);
          return false;
        }
      }
      const postalRes = validatePostalCodeDigitsOnly(
        postalCode,
        undefined,
        lang,
      );
      if (!postalRes.isValid && postalCode.trim()) {
        toast.error(postalRes.errors[0]);
        return false;
      }
      const cityRes = validateCityOptional(city, undefined, lang);
      if (!cityRes.isValid) {
        toast.error(cityRes.errors[0]);
        return false;
      }
      const countryRes = validateCountryOptional(country, undefined, lang);
      if (!countryRes.isValid) {
        toast.error(countryRes.errors[0]);
        return false;
      }
      return true;
    }
    const pnRes = validatePersonalNumberYYMMDD(personalNumber, undefined, lang);
    if (!pnRes.isValid) {
      toast.error(pnRes.errors[0]);
      return false;
    }
    const emailRes = validateEmailOptional(email, undefined, lang);
    if (!emailRes.isValid) {
      toast.error(emailRes.errors[0]);
      return false;
    }
    const phoneRes = validatePhoneDigitsOnlyMin7(phone, undefined, lang);
    if (!phoneRes.isValid) {
      toast.error(phoneRes.errors[0]);
      return false;
    }
    const postalRes = validatePostalCodeDigitsOnly(postalCode, undefined, lang);
    if (!postalRes.isValid) {
      toast.error(postalRes.errors[0]);
      return false;
    }
    const cityRes = validateCityOptional(city, undefined, lang);
    if (!cityRes.isValid) {
      toast.error(cityRes.errors[0]);
      return false;
    }
    const countryRes = validateCountryOptional(country, undefined, lang);
    if (!countryRes.isValid) {
      toast.error(countryRes.errors[0]);
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setStep("type");
    setCustomerType("PRIVATE");
    setName("");
    setEmail("");
    setPhone("");
    setPersonalNumber("");
    setStreet("");
    setPostalCode("");
    setCity("");
    setCountry("");
    setCompanyConnection("");
    setOrgNumber("");
    setVatNumber("");
    setContactPersonName("");
    setContactPersonEmail("");
    setContactPersonPhone("");
    setIsBuyer(false);
    setIsSeller(false);
  };

  const handleClose = (openState: boolean) => {
    if (!openState) {
      resetForm();
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (isCompanyType) {
      createMutation.mutate(
        {
          name: name.trim(),
          customerType,
          orgNumber: orgNumber.trim() || undefined,
          street: street.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          city: city.trim() || undefined,
          country: country.trim() || undefined,
          phone: phone.trim() || undefined,
          contactPersonName: contactPersonName.trim() || undefined,
          isBuyer,
          isSeller,
        },
        {
          onSuccess: (data) => {
            resetForm();
            onSuccess?.(data as DealerPortalCustomer);
            onClose();
            toast.success(cc.toastCreated ?? "Kund tillagd");
          },
          onError: (e: unknown) => {
            toast.error(
              (e as { message?: string })?.message ||
                (cc.toastCreateError ?? "Kunde inte skapa kund"),
            );
          },
        },
      );
      return;
    }
    createMutation.mutate(
      {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        customerType,
        personalNumber: personalNumber.trim() || undefined,
        street: street.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        city: city.trim() || undefined,
        country: country.trim() || undefined,
        companyConnection:
          customerType === "PRIVATE"
            ? undefined
            : companyConnection.trim() || undefined,
        isBuyer,
        isSeller,
      },
      {
        onSuccess: (data) => {
          resetForm();
          onSuccess?.(data as DealerPortalCustomer);
          onClose();
          toast.success(cc.toastCreated ?? "Kund tillagd");
        },
        onError: (e: unknown) => {
          toast.error(
            (e as { message?: string })?.message ||
              (cc.toastCreateError ?? "Kunde inte skapa kund"),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent {...nativeSheetProps} nested={nested}>
        <DialogHeader>
          <DialogTitle className="text-lg">
            {step === "type"
              ? (d.createCustomer?.chooseType ?? "Välj kundtyp")
              : (cc.title ?? "Lägg till ny kund")}
          </DialogTitle>
          {step === "form" && (
            <p className="text-sm text-muted-foreground pt-0.5">
              {cc.descNameRequired ??
                "Endast namn är obligatoriskt. Övriga fält är valfria."}
            </p>
          )}
        </DialogHeader>

        {step === "type" ? (
          <div className="grid grid-cols-3 gap-4 py-4">
            {CUSTOMER_TYPES.map(({ value, labelKey, Icon }) => (
              <Button
                key={value}
                type="button"
                variant="outline"
                className={`h-24 flex flex-col items-center justify-center gap-2 text-sm ${
                  customerType === value ? "border-primary bg-primary/5" : ""
                }`}
                onClick={() => {
                  setCustomerType(value);
                  setStep("form");
                }}
              >
                <Icon className="h-6 w-6" />
                <span>{c[labelKey] ?? CUSTOMER_TYPE_KEYS[labelKey]}</span>
              </Button>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 pt-1">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {isCompanyType ? (
                <>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold border-b pb-2">
                      {cc.sectionCompanyInfo ?? "Företagsinformation"}
                    </h3>
                    <div className="space-y-2">
                      <Label>{cc.companyName ?? "Företagsnamn"} *</Label>
                      <Input
                        value={name}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (NAME_LETTERS_SPACES.test(v) || v === "")
                            setName(v);
                        }}
                        placeholder={cc.placeholderCompanyName ?? "Företagets namn"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {cc.orgNumber ?? "Org.nummer"}
                      </Label>
                      <Input
                        value={orgNumber}
                        onChange={(e) => setOrgNumber(e.target.value)}
                        placeholder={cc.placeholderOrgNumber ?? "556677-8899"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {c.street ?? "Gatuadress"}
                      </Label>
                      <Input
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        placeholder={cc.placeholderStreet ?? "Exempelgatan 1"}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.postalCode ?? "Postnummer"}
                        </Label>
                        <Input
                          value={postalCode}
                          onChange={(e) => {
                            if (POSTAL_DIGITS.test(e.target.value))
                              setPostalCode(e.target.value);
                          }}
                          placeholder={cc.placeholderPostalCode ?? "12345"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.city ?? "Ort"}
                        </Label>
                        <Input
                          value={city}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (CITY_COUNTRY_LETTERS.test(v) || v === "")
                              setCity(v);
                          }}
                          placeholder={cc.placeholderCity ?? "Stockholm"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.country ?? "Land"}
                        </Label>
                        <Input
                          value={country}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (CITY_COUNTRY_LETTERS.test(v) || v === "")
                              setCountry(v);
                          }}
                          placeholder={cc.placeholderCountry ?? "Sverige"}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {c.phone ?? "Telefon (företaget)"}
                      </Label>
                      <Input
                        value={phone}
                        onChange={(e) => {
                          if (PHONE_DIGITS.test(e.target.value))
                            setPhone(e.target.value);
                        }}
                        placeholder={cc.placeholderPhone ?? "070-123 45 67"}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold border-b pb-2">
                      {cc.sectionContactPerson ?? "Kontaktperson"}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {cc.sectionContactPersonFromCompany ?? "Kontaktperson från företaget"}
                    </p>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {cc.contactPersonName ?? "Kontaktperson – Namn"}
                      </Label>
                      <Input
                        value={contactPersonName}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (NAME_LETTERS_SPACES.test(v) || v === "")
                            setContactPersonName(v);
                        }}
                        placeholder={cc.placeholderNameFirstLast ?? "Förnamn Efternamn"}
                      />
                    </div>
                    <div className="flex flex-col gap-3 pt-2">
                      <Label className="text-muted-foreground">
                        {c.role ?? "Roll"}
                      </Label>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={isBuyer}
                            onCheckedChange={(v) => setIsBuyer(!!v)}
                          />
                          <span className="text-sm">{c.buyer ?? "Köpare"}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={isSeller}
                            onCheckedChange={(v) => setIsSeller(!!v)}
                          />
                          <span className="text-sm">
                            {c.seller ?? "Säljare"}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold border-b pb-2">
                      {cc.sectionBasicInfo ?? "Grundläggande information"}
                    </h3>
                    <div className="space-y-2">
                      <Label>{c.name ?? "Namn"} *</Label>
                      <Input
                        value={name}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (NAME_LETTERS_SPACES.test(v) || v === "")
                            setName(v);
                        }}
                        placeholder={cc.placeholderNameFirstLast ?? "Förnamn Efternamn"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {c.personalNumber ?? "Personnummer"}
                      </Label>
                      <Input
                        value={personalNumber}
                        onChange={(e) =>
                          setPersonalNumber(
                            formatPersonalNumberYYMMDD(e.target.value),
                          )
                        }
                        placeholder={cc.placeholderPersonalNumber ?? "YYMMDD-XXXX"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {c.street ?? "Gatuadress"}
                      </Label>
                      <Input
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        placeholder={cc.placeholderStreet ?? "Exempelgatan 1"}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.postalCode ?? "Postnummer"}
                        </Label>
                        <Input
                          value={postalCode}
                          onChange={(e) => {
                            if (POSTAL_DIGITS.test(e.target.value))
                              setPostalCode(e.target.value);
                          }}
                          placeholder={cc.placeholderPostalCode ?? "12345"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.city ?? "Ort"}
                        </Label>
                        <Input
                          value={city}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (CITY_COUNTRY_LETTERS.test(v) || v === "")
                              setCity(v);
                          }}
                          placeholder={cc.placeholderCity ?? "Stockholm"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.country ?? "Land"}
                        </Label>
                        <Input
                          value={country}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (CITY_COUNTRY_LETTERS.test(v) || v === "")
                              setCountry(v);
                          }}
                          placeholder={cc.placeholderCountry ?? "Sverige"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold border-b pb-2">
                      {cc.sectionContactInfo ?? "Kontaktinfo"}
                    </h3>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {c.email ?? "E-post"}
                      </Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={cc.placeholderEmail ?? "namn@exempel.se"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {c.phone ?? "Telefon"}
                      </Label>
                      <Input
                        value={phone}
                        onChange={(e) => {
                          if (PHONE_DIGITS.test(e.target.value))
                            setPhone(e.target.value);
                        }}
                        placeholder={cc.placeholderPhone ?? "070-123 45 67"}
                      />
                    </div>
                    {customerType !== "PRIVATE" && (
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          {c.companyConnection ?? "Företagskoppling"}
                        </Label>
                        <Input
                          value={companyConnection}
                          onChange={(e) => setCompanyConnection(e.target.value)}
                          placeholder={cc.placeholderCompanyName ?? "Företagets namn"}
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-3 pt-2">
                      <Label className="text-muted-foreground">
                        {c.role ?? "Roll"}
                      </Label>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={isBuyer}
                            onCheckedChange={(v) => setIsBuyer(!!v)}
                          />
                          <span className="text-sm">{c.buyer ?? "Köpare"}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={isSeller}
                            onCheckedChange={(v) => setIsSeller(!!v)}
                          />
                          <span className="text-sm">
                            {c.seller ?? "Säljare"}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("type")}
                className="gap-1.5"
              >
                <ChevronLeft className="h-4 w-4" />
                {cc.back ?? c.back ?? "Tillbaka"}
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending
                  ? (c.saving ?? "Sparar...")
                  : (c.save ?? "Spara")}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
